import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const searchRef = useRef(null);
  const userMenuRef = useRef(null);

  const navigationItems = [
    {
      label: 'Operations',
      path: '/global-operations-overview-dashboard',
      icon: 'Globe',
      tooltip: 'Global Operations Overview Dashboard'
    },
    {
      label: 'Analytics',
      path: '/route-performance-analytics-dashboard',
      icon: 'BarChart3',
      tooltip: 'Route Performance Analytics Dashboard'
    },
    {
      label: 'Alerts',
      path: '/risk-management-alerts-dashboard',
      icon: 'AlertTriangle',
      tooltip: 'Risk Management & Alerts Dashboard'
    },
    {
      label: 'Tracking',
      path: '/shipment-tracking-details-dashboard',
      icon: 'Package',
      tooltip: 'Shipment Tracking & Details Dashboard'
    },
    {
      label: 'Carbon',
      path: '/carbon-offset-dashboard',
      icon: 'Leaf',
      tooltip: 'Carbon Offset Management Dashboard'
    }
  ];

  const userRoles = [
    { id: 'logistics-manager', label: 'Logistics Manager' },
    { id: 'supply-coordinator', label: 'Supply Chain Coordinator' },
    { id: 'transport-analyst', label: 'Transportation Analyst' },
    { id: 'operations-director', label: 'Operations Director' }
  ];

  const [currentUserRole, setCurrentUserRole] = useState(userRoles[0]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shipment-tracking-details-dashboard?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  };

  const handleRoleChange = (role) => {
    setCurrentUserRole(role);
    setIsUserMenuOpen(false);
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-success';
      case 'connecting': return 'text-warning';
      case 'disconnected': return 'text-error';
      default: return 'text-text-secondary';
    }
  };

  const getStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected': return 'Wifi';
      case 'connecting': return 'WifiOff';
      case 'disconnected': return 'WifiOff';
      default: return 'Wifi';
    }
  };

  const formatLastUpdate = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-surface border-b border-border z-navigation">
      <div className="px-4 lg:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Plane" size={20} color="white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-semibold text-primary">NetAir</h1>
                <p className="text-xs text-text-secondary -mt-1">Sustainable Logistics</p>
              </div>
            </div>
          </div>

          {/* Primary Navigation - Desktop */}
          <nav className="hidden md:flex items-center space-x-1">
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`
                    flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-150
                    ${isActive 
                      ? 'bg-primary text-white shadow-sm' 
                      : 'text-text-secondary hover:text-primary hover:bg-primary-50'
                    }
                  `}
                  title={item.tooltip}
                >
                  <Icon name={item.icon} size={16} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            {/* Quick Search */}
            <div className="relative" ref={searchRef}>
              {isSearchOpen ? (
                <form onSubmit={handleSearch} className="flex items-center">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search tracking number..."
                    className="w-64 px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setIsSearchOpen(false)}
                    className="ml-2 p-1 text-text-secondary hover:text-text-primary"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </form>
              ) : (
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className="p-2 text-text-secondary hover:text-primary hover:bg-primary-50 rounded-md transition-colors duration-150"
                  title="Quick search shipments"
                >
                  <Icon name="Search" size={18} />
                </button>
              )}
            </div>

            {/* Global Status Indicator */}
            <div className="flex items-center space-x-3 px-3 py-2 bg-surface-50 rounded-md border border-border-light">
              <div className="flex items-center space-x-2">
                <Icon 
                  name={getStatusIcon()} 
                  size={16} 
                  className={`${getStatusColor()} ${connectionStatus === 'connecting' ? 'animate-pulse' : ''}`}
                />
                <span className="hidden lg:inline text-xs text-text-secondary">
                  {connectionStatus === 'connected' ? 'Live' : connectionStatus}
                </span>
              </div>
              <div className="hidden lg:block w-px h-4 bg-border"></div>
              <span className="hidden lg:inline text-xs text-text-secondary">
                {formatLastUpdate(lastUpdate)}
              </span>
            </div>

            {/* User Context Switcher */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2 p-2 text-text-secondary hover:text-primary hover:bg-primary-50 rounded-md transition-colors duration-150"
              >
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} className="text-primary" />
                </div>
                <div className="hidden lg:block text-left">
                  <p className="text-sm font-medium text-text-primary">{currentUserRole.label}</p>
                </div>
                <Icon name="ChevronDown" size={16} className={`transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-surface border border-border rounded-lg shadow-lg z-dropdown animate-slide-down">
                  <div className="p-3 border-b border-border">
                    <p className="text-sm font-medium text-text-primary">Switch Role</p>
                    <p className="text-xs text-text-secondary">Change operational context</p>
                  </div>
                  <div className="py-2">
                    {userRoles.map((role) => (
                      <button
                        key={role.id}
                        onClick={() => handleRoleChange(role)}
                        className={`
                          w-full text-left px-4 py-2 text-sm transition-colors duration-150
                          ${currentUserRole.id === role.id 
                            ? 'bg-primary-50 text-primary font-medium' :'text-text-primary hover:bg-surface-50'
                          }
                        `}
                      >
                        {role.label}
                        {currentUserRole.id === role.id && (
                          <Icon name="Check" size={16} className="float-right mt-0.5" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-surface border-t border-border z-navigation">
        <div className="flex items-center justify-around py-2">
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`
                  flex flex-col items-center space-y-1 px-3 py-2 rounded-md transition-all duration-150
                  ${isActive 
                    ? 'text-primary bg-primary-50' :'text-text-secondary hover:text-primary'
                  }
                `}
                title={item.tooltip}
              >
                <Icon name={item.icon} size={20} />
                <span className="text-xs font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </header>
  );
};

export default Header;